import React from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { ThemeModeProvider } from "./contexts/ThemeModeContext";
import { BrandingProvider } from "./contexts/BrandingProvider";
import { Loading } from "./components/Loading";
import PrivateRoute from "./components/PrivateRoute/PrivateRoute";
import { getMenuLeaves } from "./components/DashboardSidebar/menuItems";
import { ORDER_STAGES } from "./pages/Order/orderStages";
import InvoicePrintPage from "./pages/Order/InvoicePrintPage";
import InvoiceHistoryPage from "./pages/Order/InvoiceHistoryPage";
import { ORDER_STATUS_VIEWS } from "./pages/Order/orderStatuses";
import { REPORTS } from "./pages/Reports/reportsConfig";
import { RESOURCES } from "./pages/Resource/resources";
import RequirePermission, { RequireFormPermission } from "./components/RequirePermission";
import HomeRedirect from "./pages/Home/HomeRedirect";
import { orderSectionKey, pageKey, reportKey } from "./utils/permissions";

const Login = React.lazy(() => import("./layouts/Login/Login"));
const DashboardSidebar = React.lazy(() =>
  import("./components/DashboardSidebar/DashboardSidebar")
);
const AdminDashboard = React.lazy(() =>
  import("./pages/AdminDashboard/AdminDashboard")
);
const ResourceListPage = React.lazy(() =>
  import("./pages/Resource/ResourceListPage")
);
const ResourceFormPage = React.lazy(() =>
  import("./pages/Resource/ResourceFormPage")
);
const ResourceDetailsPage = React.lazy(() =>
  import("./pages/Resource/ResourceDetailsPage")
);
const WalletPage = React.lazy(() => import("./pages/Resource/WalletPage"));
const ComposePage = React.lazy(() =>
  import("./pages/Communication/ComposePage")
);
const LoanCalculator = React.lazy(() => import("./pages/Loan/LoanCalculator"));
const OrderListPage = React.lazy(() => import("./pages/Order/OrderListPage"));
const OrderDetailsPage = React.lazy(() =>
  import("./pages/Order/OrderDetailsPage")
);
const ReportPage = React.lazy(() => import("./pages/Reports/ReportPage"));
const ComingSoon = React.lazy(() => import("./pages/ComingSoon/ComingSoon"));
const NotFound = React.lazy(() => import("./pages/NotFound/NotFound"));

// Hand-built pages (everything else under the dropdown groups is a
// config-driven list/form page from pages/Resource/resources.js)
const CUSTOM_ROUTES = [
  ...[...ORDER_STATUS_VIEWS, ...ORDER_STAGES].map((view) => ({
    path: view.path,
    element: <OrderListPage key={view.path} view={view} />,
    perm: orderSectionKey(view.slug),
  })),
  ...REPORTS.map((report) => ({ path: report.path, element: <ReportPage key={report.slug} report={report} />, perm: reportKey(report.slug) })),
  { path: "/dashboard/order/:id", element: <OrderDetailsPage />, perm: "order.details" },
  { path: "/dashboard/invoice-print", element: <InvoicePrintPage />, perm: "order.invoice" },
  { path: "/dashboard/invoice-history", element: <InvoiceHistoryPage />, perm: "order.invoice" },
  { path: "/dashboard/loan-calculator", element: <LoanCalculator />, perm: "loan-calculator.view" },
  { path: "/dashboard/send-sms", element: <ComposePage type="sms" />, perm: "send-sms.view" },
  { path: "/dashboard/send-email", element: <ComposePage type="email" />, perm: "send-email.view" },
];

// Pages that don't exist yet: sidebar items nothing above claims. Replace an
// entry with a real <Route> as each page gets built.
const CLAIMED_PATHS = new Set([
  "/dashboard/admin",
  ...CUSTOM_ROUTES.map(({ path }) => path),
  ...Object.values(RESOURCES)
    .filter(({ hidden }) => !hidden)
    .map(({ listPath }) => listPath),
]);
const PLACEHOLDER_ROUTES = getMenuLeaves().filter(
  ({ path }) => !CLAIMED_PATHS.has(path)
);

function App() {
  return (
    <ThemeModeProvider>
      <BrandingProvider>
        <BrowserRouter>
          <ToastContainer />
          <React.Suspense fallback={Loading}>
            <Routes>
              <Route path="/" element={<Login />} />

              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardSidebar />
                  </PrivateRoute>
                }
              >
                <Route index element={<HomeRedirect />} />
                <Route
                  path="admin"
                  element={
                    <RequirePermission perm="dashboard.view">
                      <AdminDashboard />
                    </RequirePermission>
                  }
                />

                {Object.values(RESOURCES).map(
                  ({ slug, listPath, formPath, hidden, routeParam, detailPath, walletPath }) => [
                    // lookups feed dropdowns only - no list page
                    !hidden && (
                      <Route
                        key={listPath}
                        path={listPath}
                        element={
                          <RequirePermission perm={pageKey(slug, "view")}>
                            <ResourceListPage key={slug} slug={slug} />
                          </RequirePermission>
                        }
                      />
                    ),
                    // e.g. /dashboard/currency-change-log/:id (list narrowed by id)
                    !hidden && routeParam && (
                      <Route
                        key={`${listPath}/:${routeParam.name}`}
                        path={`${listPath}/:${routeParam.name}`}
                        element={
                          <RequirePermission perm={pageKey(slug, "view")}>
                            <ResourceListPage key={slug} slug={slug} />
                          </RequirePermission>
                        }
                      />
                    ),
                    // e.g. /dashboard/loan-details/:id (read-only record view)
                    detailPath && (
                      <Route
                        key={`${detailPath}/:id`}
                        path={`${detailPath}/:id`}
                        element={
                          <RequirePermission perm={pageKey(slug, "view")}>
                            <ResourceDetailsPage key={slug} slug={slug} />
                          </RequirePermission>
                        }
                      />
                    ),
                    // e.g. /dashboard/vendor-wallet/:id (every wallet transaction as a tab of one page)
                    walletPath && (
                      <Route
                        key={`${walletPath}/:id`}
                        path={`${walletPath}/:id`}
                        element={
                          <RequirePermission perm={pageKey(slug, "wallet")}>
                            <WalletPage key={slug} slug={slug} />
                          </RequirePermission>
                        }
                      />
                    ),
                    formPath && (
                      <Route
                        key={formPath}
                        path={`${formPath}/:id`}
                        element={
                          <RequireFormPermission slug={slug}>
                            <ResourceFormPage key={slug} slug={slug} />
                          </RequireFormPermission>
                        }
                      />
                    ),
                  ]
                )}

                {CUSTOM_ROUTES.map(({ path, element, perm }) => (
                  <Route key={path} path={path} element={<RequirePermission perm={perm}>{element}</RequirePermission>} />
                ))}

                {PLACEHOLDER_ROUTES.map(({ path, name }) => (
                  <Route
                    key={path}
                    path={path}
                    element={<ComingSoon title={name} />}
                  />
                ))}

                <Route path="*" element={<NotFound />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </BrowserRouter>
      </BrandingProvider>
    </ThemeModeProvider>
  );
}

export default App;
